import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { SubmitHandler, useFormContext } from "react-hook-form";

import { Button } from "@workspace/web-ui/components/button";
import { LoginModal } from "~/features/public/components/login-modal";
import { Marquee } from "~/features/public/components/marquee";
import { OnboardingModal } from "~/features/public/components/onboarding-modal";
import { RegisterModal } from "~/features/public/components/register-modal";
import { marqueeImages } from "~/features/public/data/marquee";
import { cn } from "@workspace/web-ui/lib/utils";
import { Route } from "~/routes/(public)/_public/welcome";
import { BmiCalculate } from "~/utils/bmi-calculator";

import { BmiModal } from "../../../components/bmi-modal";
import { BmiNormalModal } from "../../../components/bmi-normal-modal";
import { ContinueNotModal } from "../../../components/continue-not-modal";
import { welcomeStepsData } from "../data";
import { WelcomeFormFieldName, WelcomeFormSchema } from "../schema";
import useAuthFormStore from "../store/use-auth-form-store";
import { useBmiNormalWeightModal } from "../store/use-bmi-normal-modal";
import { useBmiUnderWeightModal } from "../store/use-bmi-underweight-modal";
import { useContinueNotModal } from "../store/use-continue-not-store";
import { useOnboardingModal } from "../store/use-onboarding-modal";
import useRegisterFormStore from "../store/use-register-form-store";
import { useWelcomeFormStore } from "../store/use-welcome-form-store";
import { BmiStep } from "./bmi-step";
import { CheckGlpStep } from "./checkglp-step";
import { CheckMedicationStep } from "./checkmedication-step";
import { ConditionStep } from "./condition-step";
import { EventsStep } from "./events-step";
import { GenderStep } from "./gender-step";
import { HistoryStep } from "./history-step";

import { TakenGlpStep } from "./takenglp-step";
import { TimeFrameStep } from "./timeframe-step";
import { WelcomeStep } from "./welcome-step";
import { useBmiStore } from "../store/use-bmi-store";

interface FormHandlerProps {
  submit: boolean;
  activeStep: number;
  nextStep: (step?: number) => void;
  previousStep: (step?: number) => void;
}

export const WelcomeForm = ({
  activeStep,
  nextStep,
  previousStep,
}: FormHandlerProps) => {
  const { trigger, handleSubmit, reset, getValues, setValue } =
    useFormContext<WelcomeFormSchema>();
  const { updateFields, clearFields } = useWelcomeFormStore();
  const { open: openOnboardingModal } = useOnboardingModal();
  const { setStep: setAuthStep } = useAuthFormStore();
  const { setStep: setRegisterStep } = useRegisterFormStore();
  const { open: openContinueNotModal } = useContinueNotModal();
  const { open: openBmiModal } = useBmiUnderWeightModal();
  const { open: openBmiNormalModal } = useBmiNormalWeightModal();
  const [bmiresults, setBmiResults] = useState<number | undefined>(undefined);
  const [, setGlpIntake] = useState<boolean>(false);
  const { setBmi } = useBmiStore();

  const navigate = useNavigate({
    from: Route.fullPath,
  });

  const isFirstStep = activeStep === 1;

  const submitForm: SubmitHandler<WelcomeFormSchema> = () => {
    navigate({
      search: (prev) => {
        prev.submit = true;
        return prev;
      },
    });
    reset();
    clearFields();
  };

  const handleNext = async () => {
    const fields = welcomeStepsData[activeStep - 2]?.form?.map((fm) => fm.name);
    const output = await trigger(fields as WelcomeFormFieldName[], {
      shouldFocus: true,
    });
    if (!output) return;

    nextStep();
  };

  const previousTwoStep = () => {
    previousStep();
    previousStep();
  };

  const handleCalculateBmi = async () => {
    const fields = welcomeStepsData[activeStep - 2]?.form?.map((fm) => fm.name);
    const output = await trigger(fields as WelcomeFormFieldName[], {
      shouldFocus: true,
    });

    if (!output) return;

    const weight = getValues("weight");
    const height = getValues("height");

    if (weight && height) {
      const bmiresult = BmiCalculate(weight, height);
      
      if (bmiresult === undefined) {
        // Invalid height format
        return;
      }
     
      setBmiResults(bmiresult);
      setBmi(bmiresult.toString())
      if (bmiresult < 18.5) {
        openBmiModal();
      } else if (bmiresult === 18.5) {
        openBmiNormalModal();
      } else {
        nextStep();
      }
    }
  };

  const handleGlpIntake = () => {
    const fields = welcomeStepsData[activeStep - 2]?.form?.map((fm) => fm.name);
    const output = trigger(fields as WelcomeFormFieldName[], {
      shouldFocus: true,
    });
    if (!output) return;

    const glpIntake = getValues("glpIntake");
    
    if (glpIntake.includes("No")) {
      setGlpIntake(false);
      nextStep();
      nextStep();
    } else {
      setGlpIntake(true);
      nextStep();
    }
  };

  const handleOnboarding = async () => {
    const fields = welcomeStepsData[activeStep - 2]?.form?.map((fm) => fm.name);
    const output = await trigger(fields as WelcomeFormFieldName[], {
      shouldFocus: true,
    });

    if (!output) return;

    const history = getValues("history");

    if (history.includes("No")) {
      setRegisterStep(1);
      setAuthStep(1);
      openOnboardingModal();
    } else {
      openContinueNotModal();
      return;
    }
  };

  const handleHealthCondition = async () => {
    const fields = welcomeStepsData[activeStep - 2]?.form?.map((fm) => fm.name);
    const healthCondition = getValues("healthCondition");
    
    // Ensure at least one option is selected
    if (!healthCondition || healthCondition.length === 0) {
      return;
    }

    if (healthCondition.includes("None of the above") && healthCondition.length > 1) {
      setValue("healthCondition", ["None of the above"], { shouldValidate: true });
      return;
    }

    const output = await trigger(fields as WelcomeFormFieldName[], {
      shouldFocus: true,
    });
    
    if (!output) {
      openContinueNotModal();
    } else {
      nextStep();
    }
  };

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      {activeStep === 1 && <WelcomeStep />}

      {activeStep === 2 && (
        <GenderStep data={welcomeStepsData[0]!} updateFields={updateFields} />
      )}

      {activeStep === 3 && (
        <BmiStep data={welcomeStepsData[1]!} updateFields={updateFields} />
      )}

      {activeStep === 4 && (
        <TimeFrameStep data={welcomeStepsData[2]!} updateFields={updateFields} />
      )}

      {activeStep === 5 && (
        <EventsStep data={welcomeStepsData[3]!} updateFields={updateFields} />
      )}

      {activeStep === 6 && (
        <CheckMedicationStep
          data={welcomeStepsData[4]!}
          updateFields={updateFields}
        />
      )}

      {activeStep === 7 && (
        <CheckGlpStep data={welcomeStepsData[5]!} updateFields={updateFields} />
      )}

      {activeStep === 8 && (
        <TakenGlpStep data={welcomeStepsData[6]!} updateFields={updateFields} />
      )}

      {activeStep === 9 && (
        <ConditionStep data={welcomeStepsData[7]!} updateFields={updateFields} />
      )}

      {activeStep === 10 && (
        <HistoryStep data={welcomeStepsData[8]!} updateFields={updateFields} />
      )}

      {isFirstStep ? (
        <>
          <div className="mx-auto w-full px-4 sm:max-w-[524px] lg:max-w-[624px]">
            <Button
              type="button"
              onClick={() => nextStep(2)}
              size={"xl"}
              className="mx-auto w-full cursor-pointer rounded-xl bg-[#4272DD] px-3 py-3 text-center text-base sm:px-4 sm:py-4 sm:text-lg lg:text-xl"
            >
              Get started
            </Button>
          </div>
          <div className="mt-3 sm:mt-4 lg:mt-[46px]">
            <Marquee images={marqueeImages} />
          </div>
        </>
      ) : (
        <div className="fixed bottom-0 left-0 right-0 z-10 border border-t border-t-[#0000001A] bg-white shadow-[0px_-2px_10px_0px_#00000014]">
          <div className="mx-auto flex w-full items-center justify-between px-4 py-3 sm:max-w-[524px] sm:px-6 sm:py-4 lg:max-w-[624px] lg:px-8 lg:py-2">
            <Button
              type="button"
              variant="outline"
              onClick={
                activeStep === 9 ? previousTwoStep : () => previousStep()
              }
              className="h-[40px] w-[120px] cursor-pointer rounded-2xl border-[1px] border-solid border-[#DCDCDE] bg-transparent px-3 py-2 text-sm text-[#7F8493] shadow-[0px_1px_2px_0px_#0000001A] sm:h-[48px] sm:w-[120px] sm:px-4 sm:text-base lg:h-[57px] lg:w-[144px] lg:px-6 lg:py-3 lg:text-lg"
            >
              Back
            </Button>
            <Button
              type="button"
              onClick={
                activeStep === 7
                  ? handleGlpIntake
                  : activeStep === 3
                    ? handleCalculateBmi
                    : activeStep === 9
                      ? handleHealthCondition
                      : activeStep === 10
                        ? handleOnboarding
                        : handleNext
              }
              className={cn(
                "h-[40px] w-[120px] cursor-pointer rounded-2xl bg-[#4272DD] px-3 py-2 text-sm text-white shadow-[0px_1px_2px_0px_#0000001A] sm:h-[48px] sm:w-[120px] sm:px-4 sm:text-base lg:h-[57px] lg:w-[144px] lg:px-8 lg:py-4 lg:text-lg",
                activeStep === 10 && "w-[200px] sm:w-[220px] lg:w-[261px]",
              )}
            >
              {activeStep === 10 ? "Pay for consultation" : "Continue"}
            </Button>
          </div>
        </div>
      )}

      <OnboardingModal />
      <ContinueNotModal text="We currently do not offer treatment to patients with these conditions. Please consult a healthcare professional for assistance." />
      <RegisterModal />
      <LoginModal />
      <BmiModal result={bmiresults ?? 0} />
      <BmiNormalModal result={bmiresults ?? 0} />
    </form>
  );
};
